<script setup lang="ts">
import { useRouter } from "vue-router";

import LeDictionaryFavoriteToggle from "@/components/dictionary-selector/LeDictionaryFavoriteToggle.vue";
import LeDictionaryUpsellStateIcon from "@/components/dictionary-selector/LeDictionaryUpsellStateIcon.vue";
import LeResultsListButton from "@/components/dictionary-selector/LeResultsListButton.vue";
import { SearchRouteHelper } from "@/helpers";
import { useUserStore } from "@/stores";
import type { Dictionary } from "@/types";

interface Props {
  dictionary: Dictionary;
  isSelected?: boolean;
  isFavorite?: boolean;
  showFavoriteToggle?: boolean;
  isUpsell?: boolean;
  isUnlocked?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  isSelected: false,
  isFavorite: false,
  showFavoriteToggle: false,
  isUpsell: false,
  isUnlocked: false
});

const emit = defineEmits<{
  (event: "select", dictionaryId: string): void;
  (event: "favorite", dictionaryId: string): void;
  (event: "unfavorite", dictionaryId: string): void;
}>();

const onSelect = () => {
  emit("select", props.dictionary.id);
};

const searchRouteHelper = new SearchRouteHelper(useRouter(), useUserStore().userData);
</script>

<template>
  <LeResultsListButton
    class="le-results-list-item"
    :is-selected="props.isSelected"
    :route="searchRouteHelper.generateDictionarySearchUrl(props.dictionary.id)"
    @click="onSelect()"
  >
    <template #content>
      <span class="le-results-list-item__button-content">
        <span class="le-results-list-item__title">{{ props.dictionary.title }}</span>
        <span class="le-results-list-item__publisher">{{ props.dictionary.publisher }}</span>
      </span>
    </template>

    <template #icon>
      <LeDictionaryFavoriteToggle
        v-if="props.showFavoriteToggle"
        :is-favorite="props.isFavorite"
        class="le-results-list-item__icon-content"
        @favorite="emit('favorite', props.dictionary.id)"
        @unfavorite="emit('unfavorite', props.dictionary.id)"
      />
      <LeDictionaryUpsellStateIcon
        v-else-if="props.isUpsell"
        :is-unlocked="props.isUnlocked"
        :is-selected="props.isSelected"
        class="le-results-list-item__icon-content le-results-list-item__icon-content--presentation"
      />
    </template>
  </LeResultsListButton>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-results-list-item {
  &__button-content {
    display: grid;
    grid-auto-rows: 18px;
    grid-template-areas:
      "title"
      "publisher";
    grid-template-columns: minmax(0, 1fr);
    align-items: center;
    padding-right: 32px;
    column-gap: 4px;
  }

  &__title {
    grid-area: title;
    overflow: hidden;
    font-family: @font-family-body;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    .text-fade();
  }

  &__publisher {
    grid-area: publisher;
    overflow: hidden;
    color: @colors[@neutrals][300];
    font-family: @font-family-body;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    .text-fade();
  }

  &__icon-content {
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);

    &--presentation {
      pointer-events: none;
    }
  }
}
</style>
